import React, { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  X, Bell, BellOff, BellRing, Plus, Trash2, Tag, Rss, Loader2,
} from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { useNewsSubscriptions } from '../../../hooks/useNewsSubscriptions'
import { useNewsSources } from '../../../hooks/useNews'
import { useConfirm } from '../../../contexts/ConfirmDialogContext'
import { cn } from '../../../lib/utils'

// Aggregated sheet for managing all news subscriptions in one place.
// Two sections — Sources (e.g. r/MachineLearning, AJ+ عربى) and Topics
// (free-text keywords matched against article tags + title). Adding a
// topic happens inline at the bottom of the Topics section; source
// subscriptions are added via the per-source page (already wired).

const MODES = [
  { value: 'daily',   icon: Bell,     labelKey: 'newsSource.modeDaily' },
  { value: 'instant', icon: BellRing, labelKey: 'newsSource.modeInstant' },
]

export default function NewsSubscriptionsSheet({ open, onClose }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const subs = useNewsSubscriptions()
  const sourcesHook = useNewsSources()
  const [newTopic, setNewTopic] = useState('')
  const [newMode, setNewMode] = useState('daily')
  const [adding, setAdding] = useState(false)

  // Group rows by source vs topic.
  const sourceRows = useMemo(
    () => subs.rows.filter((r) => r.source_id).map((r) => {
      const src = sourcesHook.sources.find((s) => s.id === r.source_id)
      return { ...r, sourceName: src?.name || r.source_id, sourceSlug: src?.slug }
    }),
    [subs.rows, sourcesHook.sources],
  )
  const topicRows = useMemo(
    () => subs.rows.filter((r) => r.tag),
    [subs.rows],
  )

  const handleAddTopic = async (e) => {
    e?.preventDefault?.()
    const tag = newTopic.trim().toLowerCase()
    if (!tag) return
    setAdding(true)
    const ok = await subs.subscribe({ tag, notify_mode: newMode })
    setAdding(false)
    if (ok) {
      setNewTopic('')
    }
  }

  const handleMode = async (row, mode) => {
    if (mode === 'off') {
      const ok = await confirm({
        title: t('common.confirmDelete'),
        message: row.tag || row.sourceName,
        destructive: true,
      })
      if (!ok) return
      await subs.unsubscribe(row.source_id ? { source_id: row.source_id } : { tag: row.tag })
    } else {
      await subs.subscribe({
        source_id: row.source_id || null,
        tag: row.tag || null,
        notify_mode: mode,
      })
    }
  }

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/70 flex flex-col"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-background mt-auto rounded-t-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-input flex-shrink-0">
          <BellRing className="w-4 h-4 text-app-primary-500" />
          <h2 className="flex-1 text-base font-semibold text-foreground" dir="auto">
            {t('newsSubs.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {subs.loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <>
              {/* Sources section */}
              <section className="space-y-2">
                <div className="flex items-center gap-2">
                  <Rss className="w-4 h-4 text-muted-foreground" />
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex-1" dir="auto">
                    {t('newsSubs.sources')}
                    {sourceRows.length > 0 && <span className="ms-1 opacity-70">({sourceRows.length})</span>}
                  </h3>
                </div>
                {sourceRows.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic px-1" dir="auto">
                    {t('newsSubs.sourcesEmpty')}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {sourceRows.map((r) => (
                      <SubRow
                        key={r.id}
                        label={r.sourceName}
                        icon={Rss}
                        currentMode={r.notify_mode}
                        onMode={(m) => handleMode(r, m)}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Topics section */}
              <section className="space-y-2">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-muted-foreground" />
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex-1" dir="auto">
                    {t('newsSubs.topics')}
                    {topicRows.length > 0 && <span className="ms-1 opacity-70">({topicRows.length})</span>}
                  </h3>
                </div>
                {topicRows.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic px-1" dir="auto">
                    {t('newsSubs.topicsEmpty')}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {topicRows.map((r) => (
                      <SubRow
                        key={r.id}
                        label={r.tag}
                        icon={Tag}
                        currentMode={r.notify_mode}
                        onMode={(m) => handleMode(r, m)}
                      />
                    ))}
                  </div>
                )}

                {/* Add new topic */}
                <form onSubmit={handleAddTopic} className="space-y-2 p-3 rounded-md border border-input bg-muted/30">
                  <p className="text-[11px] text-muted-foreground" dir="auto">
                    {t('newsSubs.addTopicHint')}
                  </p>
                  <input
                    type="text"
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    placeholder={t('newsSubs.topicPlaceholder')}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  />
                  <div className="flex gap-1.5">
                    {MODES.map((m) => {
                      const Icon = m.icon
                      return (
                        <button
                          key={m.value}
                          type="button"
                          onClick={() => setNewMode(m.value)}
                          className={cn(
                            'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border',
                            newMode === m.value
                              ? 'bg-app-primary-500 text-white border-app-primary-500'
                              : 'border-input text-muted-foreground hover:bg-accent',
                          )}
                        >
                          <Icon className="w-3 h-3" />
                          {t(m.labelKey)}
                        </button>
                      )
                    })}
                    <button
                      type="submit"
                      disabled={adding || !newTopic.trim()}
                      className="ms-auto inline-flex items-center gap-1 h-9 px-3 rounded-md bg-app-primary-500 text-white text-sm font-medium disabled:opacity-60"
                    >
                      {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      {t('newsSubs.addTopic')}
                    </button>
                  </div>
                </form>
              </section>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

function SubRow({ label, icon: Icon, currentMode, onMode }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-input">
      <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
      <p className="flex-1 min-w-0 text-sm text-foreground truncate" dir="auto">{label}</p>
      <div className="flex items-center gap-1 flex-shrink-0">
        {MODES.map((m) => {
          const I = m.icon
          const active = currentMode === m.value
          return (
            <button
              key={m.value}
              type="button"
              onClick={() => onMode(m.value)}
              aria-label={t(m.labelKey)}
              title={t(m.labelKey)}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                active
                  ? 'bg-app-primary-500 text-white'
                  : 'text-muted-foreground hover:bg-accent',
              )}
            >
              <I className="w-3.5 h-3.5" />
            </button>
          )
        })}
        <button
          type="button"
          onClick={() => onMode('off')}
          aria-label={t('common.delete')}
          className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-red-600"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}
